/**
 * Shared display formatters. subscription_cards.js and subscription_select.js
 * used to carry their own formatPrice with observably different output
 * (toFixed vs toLocaleString) — the same monthly price must render the same
 * everywhere a customer sees it.
 */

/**
 * Format a price for display: two decimals with a trailing ".00" trimmed
 * ("187.50", "249"). Deterministic — not locale-dependent, so the output is
 * identical in every browser.
 *
 * @param {*} amount The price.
 * @return {string} The formatted price, or '' when the amount is unknown
 *                  (null/undefined/non-numeric) so callers can fall back.
 */
export function formatPrice( amount ) {
    if ( amount == null || amount === '' ) {
        return '';
    }
    const value = Number( amount );
    if ( ! isFinite( value ) ) {
        return '';
    }
    return value.toFixed( 2 ).replace( /\.00$/, '' );
}

/**
 * Escape a value for interpolation into an HTML text or double/single-quoted
 * attribute context. The blocks render API-sourced values (product names,
 * SSIDs, DHCP hostnames, …) into concatenated HTML in several places; every
 * one of them must route through this ONE helper — the per-file copies it
 * replaces had three different escape sets.
 *
 * @param {*} value The raw value (null/undefined become '').
 * @return {string} The escaped string.
 */
export function escapeHtml( value ) {
    return String( value == null ? '' : value )
        .replace( /&/g, '&amp;' )
        .replace( /</g, '&lt;' )
        .replace( />/g, '&gt;' )
        .replace( /"/g, '&quot;' )
        .replace( /'/g, '&#039;' );
}

//Alias for attribute contexts — same escape set, clearer call sites.
export const escapeAttr = escapeHtml;

/**
 * Danish display date ("30.7.2026" per da-DK). One definition — the overview
 * card and the selection card must render the same product date identically.
 *
 * @param {*}      value    Date string/timestamp.
 * @param {number} offsetMs Optional offset applied before formatting (the
 *                          subscription-select end date subtracts 12h to show
 *                          the API's exclusive boundary as an inclusive day).
 * @return {string} Formatted date, or the raw value when unparseable.
 */
export function formatDate( value, offsetMs = 0 ) {
    const date = new Date( new Date( value ).getTime() + offsetMs );
    if ( isNaN( date.getTime() ) ) {
        return String( value );
    }
    return date.toLocaleDateString( 'da-DK' );
}

/**
 * invoiceBy → Danish label. One vocabulary for the overview card and the
 * payments page (they had two drifting maps); per-page wording differences
 * go through overrides.
 *
 * @param {string} invoiceBy Customer invoiceBy value.
 * @param {Object} overrides Optional per-page label overrides.
 * @return {string} The label ('Faktura' for unmapped values).
 */
const PAYMENT_METHOD_LABELS = {
    bs: 'Betalingsservice',
    betalingsservice: 'Betalingsservice',
    ean: 'EAN-Nummer',
    email: 'Faktura på email',
    card: 'Automatisk kortbetaling',
    mobilepay: 'MobilePay',
    paper: 'Faktura',
};

export function paymentMethodLabel( invoiceBy, overrides ) {
    const map = { ...PAYMENT_METHOD_LABELS, ...( overrides || {} ) };
    return map[ invoiceBy ] || 'Faktura';
}
